import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';

export function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <main className="py-16">
        <Container size="md">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: March 19, 2026
                </p>
              </div>

              <div className="prose prose-lg max-w-none">

                {/* 1. Agreement to Terms */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. Agreement to Terms</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms of Service ("Terms") constitute a legally binding agreement between you and Zero Point Studio d.o.o.
                    ("we," "our," or "us"), governing your access to and use of the Cascady platform and all related services
                    (collectively, the "Service").
                  </p>
                  <p className="text-muted-foreground">
                    By creating an account or using the Service, you agree to be bound by these Terms, our{' '}
                    <a href="/privacy" className="text-primary-600 underline">Privacy Policy</a>, and our{' '}
                    <a href="/refund-policy" className="text-primary-600 underline">Refund Policy</a>. If you do not agree to these Terms, do not use the Service.
                  </p>
                </section>

                {/* 2. Company Information */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Company Information</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Company:</strong> Zero Point Studio d.o.o.</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground mb-2"><strong>Phone:</strong> +385 91 722 8780</p>
                    <p className="text-muted-foreground mb-2"><strong>Director:</strong> Andrej Šimunaj</p>
                    <p className="text-muted-foreground"><strong>Registration Date:</strong> April 24, 2024</p>
                  </div>
                </section>

                {/* 3. Service Description */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. Service Description</h2>
                  <p className="text-muted-foreground mb-4">
                    Cascady is a visual AI creative workflow platform that provides:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>A visual canvas for building AI image and video generation workflows</li>
                    <li>Access to multiple third-party AI models for creative content generation</li>
                    <li>Reference image upload and management for guiding AI outputs</li>
                    <li>Credit-based usage metering for AI generations</li>
                    <li>Organization-based collaboration and asset management</li>
                    <li>Gallery and asset library for storing generated content</li>
                  </ul>
                  <p className="text-muted-foreground">
                    We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.
                  </p>
                </section>

                {/* 4. Account Registration */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. Account Registration</h2>
                  <p className="text-muted-foreground mb-4">To use the Service, you must:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Be at least 18 years of age</li>
                    <li>Provide accurate, complete registration information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of any unauthorized access to your account</li>
                  </ul>
                  <p className="text-muted-foreground">
                    You are responsible for all activity that occurs under your account, including any actions taken by team members
                    within your organization.
                  </p>
                </section>

                {/* 5. Credit System and Billing */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Credit System and Billing</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.1 Credit-Based Usage</h3>
                  <p className="text-muted-foreground mb-4">
                    AI generations on Cascady consume credits. Each AI model has a different credit cost based on the
                    computational resources required. Credits are deducted in real time upon successful generation.
                    Detailed credit costs for each model are displayed in the application and on our pricing page.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.2 Subscription Plans</h3>
                  <p className="text-muted-foreground mb-4">
                    Paid plans include a monthly allocation of credits. Unused credits do not roll over to the next billing period — they
                    expire at the end of each billing cycle. Plans are available on monthly and annual billing intervals.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.3 Payment</h3>
                  <p className="text-muted-foreground mb-4">
                    All payments are processed through Stripe. By subscribing, you authorize recurring charges to your payment method.
                    You are responsible for all applicable taxes.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">5.4 Price Changes</h3>
                  <p className="text-muted-foreground">
                    We may change pricing with at least 30 days' notice via email. Continued use of the Service after a price change
                    takes effect constitutes acceptance of the new pricing.
                  </p>
                </section>

                {/* 6. User Content and Intellectual Property */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. User Content and Intellectual Property</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">6.1 Your Content</h3>
                  <p className="text-muted-foreground mb-4">
                    You retain ownership of content you upload to the Service (reference images, prompts, and other materials).
                    By uploading content, you grant us a limited license to store, process, and transmit that content solely for
                    the purpose of providing the Service to you.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">6.2 AI-Generated Content</h3>
                  <p className="text-muted-foreground mb-4">
                    Ownership of AI-generated images and video is subject to the terms of the underlying AI model providers.
                    We do not claim ownership of content generated through the Service. However, you are solely responsible for
                    ensuring your use of AI-generated content complies with applicable laws and the terms of the AI model providers.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">6.3 Our Intellectual Property</h3>
                  <p className="text-muted-foreground">
                    The Service, including its software, design, features, documentation, and branding, is owned by Zero Point Studio d.o.o.
                    and protected by copyright, trademark, and other intellectual property laws. These Terms grant you a limited,
                    non-exclusive, non-transferable, revocable license to use the Service during your subscription.
                  </p>
                </section>

                {/* 7. Acceptable Use */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. Acceptable Use Policy</h2>
                  <p className="text-muted-foreground mb-4">You agree not to use the Service to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Generate or distribute illegal, harmful, threatening, abusive, or hateful content</li>
                    <li>Create deepfakes, non-consensual intimate imagery, or content that impersonates real individuals without consent</li>
                    <li>Generate content that exploits or endangers minors in any way</li>
                    <li>Violate intellectual property rights of any third party</li>
                    <li>Attempt to reverse engineer, decompile, or disassemble the Service</li>
                    <li>Circumvent usage limits, rate limiting, or credit metering</li>
                    <li>Use automated tools or bots to access the Service without authorization</li>
                    <li>Interfere with the Service infrastructure or other users' experience</li>
                    <li>Resell or redistribute access to the Service without authorization</li>
                  </ul>
                  <p className="text-muted-foreground">
                    Violation of this policy may result in immediate suspension or termination of your account without refund.
                  </p>
                </section>

                {/* 8. Third-Party AI Models */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">8. Third-Party AI Models</h2>
                  <p className="text-muted-foreground mb-4">
                    Cascady integrates with multiple third-party AI model providers. By using these models through our Service,
                    you also agree to comply with each provider's terms of use and acceptable use policies.
                  </p>
                  <p className="text-muted-foreground mb-4">
                    We do not control the output quality, accuracy, or behavior of third-party AI models. AI-generated content may
                    contain errors, artifacts, or unexpected results. We are not liable for the output of any third-party AI model.
                  </p>
                  <p className="text-muted-foreground">
                    Availability of specific AI models may change without notice as providers update, restrict, or discontinue their services.
                  </p>
                </section>

                {/* 9. Service Availability */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">9. Service Availability</h2>
                  <p className="text-muted-foreground">
                    The Service is provided "as is" without guaranteed uptime. Scheduled and unscheduled maintenance may temporarily
                    interrupt access. We make reasonable efforts to maintain availability but do not guarantee that the Service will
                    be uninterrupted, error-free, or secure at all times.
                  </p>
                </section>

                {/* 10. Service Cessation Guarantee */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">10. Service Cessation Guarantee</h2>
                  <p className="text-muted-foreground">
                    In the event that we are unable to continue operations or choose to permanently discontinue the Service,
                    we will provide a refund for all unused credits on a pro-rata basis. This guarantee applies regardless of
                    any other refund limitations in these Terms or our Refund Policy.
                  </p>
                </section>

                {/* 11. Cancellation and Termination */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">11. Cancellation and Termination</h2>

                  <h3 className="text-xl font-medium text-foreground mb-3">11.1 Cancellation by You</h3>
                  <p className="text-muted-foreground mb-4">
                    You may cancel your subscription at any time through the Stripe customer portal accessible from your account settings.
                    Upon cancellation, your subscription remains active until the end of the current billing period.
                    No further charges will be applied after cancellation.
                  </p>

                  <h3 className="text-xl font-medium text-foreground mb-3">11.2 Termination by Us</h3>
                  <p className="text-muted-foreground mb-4">We may terminate or suspend your account if you:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Violate these Terms or the Acceptable Use Policy</li>
                    <li>Fail to pay for your subscription after multiple attempts</li>
                    <li>Engage in suspected fraud or abuse</li>
                    <li>Are required to be removed by legal or regulatory order</li>
                  </ul>

                  <h3 className="text-xl font-medium text-foreground mb-3">11.3 Effect of Termination</h3>
                  <p className="text-muted-foreground">
                    Upon termination, your access to the Service ceases immediately. Your generated content and account data may be
                    deleted in accordance with our data retention policy. Sections of these Terms that should reasonably survive
                    termination (such as intellectual property, limitation of liability, and indemnification) will remain in effect.
                  </p>
                </section>

                {/* 12. Limitation of Liability */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">12. Limitation of Liability</h2>
                  <p className="text-muted-foreground mb-4 uppercase font-medium text-sm">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZERO POINT STUDIO D.O.O. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
                  </p>
                  <p className="text-muted-foreground mb-4">This includes, but is not limited to:</p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>Loss of profits, revenue, data, or business opportunities</li>
                    <li>Inaccurate, incomplete, or unexpected AI-generated output</li>
                    <li>Failures or outages of third-party AI model providers</li>
                    <li>Unauthorized access to your account due to your credential management</li>
                  </ul>
                  <p className="text-muted-foreground">
                    Our total liability for any claim arising under these Terms shall not exceed the total amount you paid to us
                    in the 12 months preceding the claim.
                  </p>
                </section>

                {/* 13. Disclaimer of Warranties */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">13. Disclaimer of Warranties</h2>
                  <p className="text-muted-foreground uppercase font-medium text-sm">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
                    INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY,
                    RELIABILITY, COMPLETENESS, NON-INFRINGEMENT, UNINTERRUPTED OPERATION, OR CORRECTION OF DEFECTS.
                  </p>
                </section>

                {/* 14. Indemnification */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">14. Indemnification</h2>
                  <p className="text-muted-foreground">
                    You agree to indemnify, defend, and hold harmless Zero Point Studio d.o.o., its directors, employees, and agents
                    from and against any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your
                    use of the Service, violation of these Terms, or infringement of any third-party rights.
                  </p>
                </section>

                {/* 15. Governing Law */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">15. Governing Law and Jurisdiction</h2>
                  <p className="text-muted-foreground mb-4">
                    These Terms shall be governed by and construed in accordance with the laws of the Republic of Croatia,
                    without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject
                    to the exclusive jurisdiction of the courts in Zagreb, Croatia.
                  </p>
                  <p className="text-muted-foreground">
                    If you are a consumer in the European Union, you retain the right to bring proceedings in your country of residence
                    in accordance with applicable EU consumer protection regulations.
                  </p>
                </section>

                {/* 16. Changes to Terms */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">16. Changes to Terms</h2>
                  <p className="text-muted-foreground">
                    We reserve the right to modify these Terms at any time. Material changes will be communicated with at least
                    30 days' notice via email or a prominent notice on the Service. Your continued use of the Service after changes
                    take effect constitutes acceptance of the revised Terms.
                  </p>
                </section>

                {/* 17. Severability */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">17. Severability</h2>
                  <p className="text-muted-foreground">
                    If any provision of these Terms is found to be invalid or unenforceable, that provision will be replaced with
                    a valid, enforceable provision that most closely reflects the original intent. The remaining provisions will
                    continue in full force and effect.
                  </p>
                </section>

                {/* 18. Entire Agreement */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">18. Entire Agreement</h2>
                  <p className="text-muted-foreground">
                    These Terms, together with our <a href="/privacy" className="text-primary-600 underline">Privacy Policy</a> and{' '}
                    <a href="/refund-policy" className="text-primary-600 underline">Refund Policy</a>, constitute the entire agreement
                    between you and Zero Point Studio d.o.o. regarding the Service, and supersede all prior understandings or agreements.
                  </p>
                </section>

                {/* 19. Contact */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">19. Contact Information</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have questions about these Terms, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Company:</strong> Zero Point Studio d.o.o.</p>
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground mb-2"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                    <p className="text-muted-foreground"><strong>Phone:</strong> +385 91 722 8780</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
